/**
 * Element styles for use on forms. These exist here for two reasons:
 * 1) react-form-hook uses refs, so we can't use the shared forms.input
 * 2) for dev fast refresh to work, modules must only export components
 */

import type { CSSObjectWithLabel, GroupBase, StylesConfig, Theme } from 'react-select';

// Input chrome. `focus:outline-none` suppresses the global :focus-visible
// outline catchall on keyboard focus — the rest gray-300 ring already flips
// to primary-600 via `focus:ring-primary-600`, so the catchall would only
// stack a second indicator on top.
//
// `font-sans text-base` is explicit: native <input> elements do NOT inherit
// font-family from body, and most browsers default to ~13px system-ui.
// Without these classes, the input value renders in a different font + size
// than its label, which reads as a visual mismatch. Per the Body Floor Rule
// (DESIGN.md), primary content is text-base (16px); inputs carry primary
// content (the operator's data), so they hold the floor.
//
// Placeholder drops to text-sm (Label sizing, per DESIGN.md typography) so an
// empty input reads at the same visual weight as the SearchInput placeholder
// overlay (also text-sm). Without this, the 16px placeholder in an empty form
// input looks heavier than the 14px placeholder in the search bar next to it.
const inputBaseClass =
  'block rounded-md border-0 py-1.5 font-sans text-base text-gray-900 shadow-xs ring-1 ring-inset placeholder:text-sm placeholder:text-gray-400 focus:outline-none focus:ring-primary-600 p-2 disabled:bg-gray-100';
export const inputClass = `${inputBaseClass} ring-gray-300`;
export const inputInvalidClass = `${inputBaseClass} ring-red-300`;

// checkbox styles. @tailwindcss/forms paints focus as a 2px ring + 2px offset
// by default — tighten to 1px / 1px so the focus indicator matches the rest
// of the thin-dark vocabulary.
const checkboxBaseClass =
  'h-4 w-4 rounded-sm border-gray-300 text-primary-600 focus:ring-1 focus:ring-offset-1 focus:ring-primary-600';
export const checkboxClass = `${checkboxBaseClass} ring-gray-300`;
export const checkboxInvalidClass = `${checkboxBaseClass} ring-red-300`;

// Inputs squared off on the right to allow for action buttons. `focus:outline-none`
// per inputClass note. `font-sans` is explicit for the same reason — native
// <input> doesn't inherit it. Placeholder uses text-sm (Label sizing) so it
// matches the rest of the input vocabulary; see `inputBaseClass` for the why.
const buttonInputBaseClass =
  'block w-full rounded-none rounded-l-md border-0 py-1.5 px-3 font-sans text-gray-900 ring-1 ring-inset placeholder:text-sm placeholder:text-gray-400 focus:outline-none focus:ring-primary-600 sm:text-sm sm:leading-6';
export const buttonInputClass = `${buttonInputBaseClass} ring-gray-300`;
export const buttonInputInvalidClass = `${buttonInputBaseClass} ring-red-300`;

// button for use on the right side of the above squared input
const actionButtonBaseClass =
  'relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-primary-600';
export const actionButtonClass = `${actionButtonBaseClass} ring-gray-300`;
export const actionButtonInvalidClass = `${actionButtonBaseClass} ring-red-300`;

// react-select shared theme + base styles
//
// Two react-select wrappers in the app (`forms.tsx MultiSelect` and
// `products/store-labels-input.tsx`) had drifted into independent style
// configs. Everything that should look the same across them lives here.

/**
 * Brand-coupled react-select theme. `primary` is the focus-border color —
 * `--color-focus-ring` (primary-600) to match the thin-dark focus ring on
 * native inputs. `primary25` is the option-row hover background — gray-300
 * (deliberately not emerald, so a hovered row doesn't look pre-selected
 * before the click lands). Bumped from gray-100 to gray-200 to gray-300
 * across the v6 contrast-hardening iterations: the first two were still
 * collapsing into white under Chrome's reduced-contrast emulator; gray-300
 * finally reads. Stays grey, never the brand emerald — that's reserved for
 * the actually-selected option (`primary`, drawn solid below).
 * `neutral20`/`neutral30` are the rest-state border, pulled from
 * `--color-toolbar-edge` to track every other toolbar surface.
 */
export function brandedSelectTheme(theme: Theme): Theme {
  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary: 'var(--color-focus-ring)',
      primary75: '#34d399',
      primary50: '#a7f3d0',
      primary25: '#d1d5db',
      neutral20: 'var(--color-toolbar-edge)',
      neutral30: 'var(--color-toolbar-edge)',
    },
  };
}

/**
 * Base `styles` config every react-select instance in the app should consume.
 * 40px min-height, portal z-index above sticky overlays, and `outline: none`
 * on the inner combobox input so the browser's default blue focus ring can't
 * bleed through on mouse focus. Focus chrome is the brand `primary` border
 * alone (no extra glow shadow) — that's a single 1px primary-600 line, the
 * same treatment native inputs get from `inputClass`.
 *
 * MultiValue pills are tinted toward the brand (primary-50 fill, primary-700
 * label and remove icon). The earlier react-select gray was visually
 * disconnected from the rest of the system: the selected option in the
 * dropdown reads as solid emerald (a strong "this is your value" signal),
 * but the pills representing those values then dropped to a neutral gray.
 * The soft tint ties pills back to the family while staying quiet enough to
 * survive a multi-select with many selected items (no flood of saturated
 * emerald).
 *
 * Per-component specifics (option rendering, etc.) are added by spreading
 * this and overriding specific keys.
 */
export function baseSelectStyles<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(): StylesConfig<Option, IsMulti, Group> {
  return {
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      width: 'max-content',
      minWidth: '100%',
    }),
    menuPortal: (base: CSSObjectWithLabel) => ({
      ...base,
      zIndex: 9999,
    }),
    control: (base: CSSObjectWithLabel, state) => ({
      ...base,
      minHeight: '40px',
      boxShadow: 'none',
      '&:hover': {
        borderColor: state.isFocused ? 'var(--color-focus-ring)' : 'var(--color-toolbar-edge)',
      },
    }),
    input: (base: CSSObjectWithLabel) => ({
      ...base,
      '& input': {
        boxShadow: 'none !important',
        outline: 'none !important',
      },
    }),
    valueContainer: (base: CSSObjectWithLabel) => ({
      ...base,
      padding: '0 6px',
    }),
    multiValue: (base: CSSObjectWithLabel) => ({
      ...base,
      backgroundColor: 'var(--color-primary-50)',
      borderRadius: '4px',
    }),
    multiValueLabel: (base: CSSObjectWithLabel) => ({
      ...base,
      color: 'var(--color-primary-700)',
      fontWeight: 500,
    }),
    multiValueRemove: (base: CSSObjectWithLabel) => ({
      ...base,
      color: 'var(--color-primary-700)',
      borderRadius: '0 4px 4px 0',
      ':hover': {
        backgroundColor: 'var(--color-primary-100)',
        color: 'var(--color-primary-800)',
      },
    }),
  };
}
